import { useTranslation } from 'react-i18next';

import { useEffect, useState } from 'react';

import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import { IconArchive, IconTrash } from '@tabler/icons-react';

import { Button, ColorSwatch, Group, TaskDetailDrawerView } from '@optiro/ui';

import { Alert } from '@/components/Alert/Alert';
import { ProjectBadge } from '@/components/ProjectBadge/ProjectBadge';
import { ProjectMilestoneSelect } from '@/components/ProjectMilestoneSelect';
import { SelectLocation } from '@/components/TaskForm/SelectLocation';
import { SelectTaskCategory } from '@/components/TaskForm/SelectTaskCategory';
import { SelectTrade } from '@/components/TaskForm/SelectTrade';
import { TaskStatus } from '@/components/TaskStatus';
import { useCompanyAccountCategories } from '@/hooks/useCompanyAccountCategories/useCompanyAccountCategories';
import { useLocations } from '@/hooks/useLocations/useLocations';
import { useProjectMilestones } from '@/hooks/useProjectMilestones';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import {
  useArchiveTask,
  useDeleteTask,
  useTask,
  useUnarchiveTask,
  useUpdateTask,
} from '@/hooks/useTasks';
import { SourceText, useExtractionSources } from '@/modules/FileExtraction';
import { EconomyTab } from '@/modules/Tasks/EconomyTab';
import { Overview } from '@/modules/Tasks/Overview';
import { TimeTab } from '@/modules/Tasks/TimeTab';
import { Task } from '@/types/types';

// ─── TaskDrawer ───────────────────────────────────────────────────────────────
// Adapter: fetches task data + wires mutations, hands presentation to
// TaskDetailDrawerView (@optiro/ui organism).

type Props = {
  opened: boolean;
  onClose: () => void;
  taskId: Task['id'];
  initialTab?: string;
};

export const TaskDrawer = ({ opened, onClose, taskId, initialTab }: Props) => {
  const { t } = useTranslation('task');
  const { t: tExtraction } = useTranslation('extraction');
  const { data: task } = useTask(taskId);
  const { data: sources } = useExtractionSources(
    task?.project_id,
    'task',
    task ? [Number(task.id)] : []
  );
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask(task as Task);
  const archive = useArchiveTask(taskId);
  const unarchive = useUnarchiveTask(taskId);
  const [deleteOpen, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState(initialTab ?? 'overview');

  const { data: categories } = useTaskCategories();
  const { data: trades } = useCompanyAccountCategories();
  const { data: locations } = useLocations(task?.project_id ? Number(task.project_id) : 0, {
    enabled: !!task?.project_id,
  });
  const { data: milestones } = useProjectMilestones(task?.project_id ?? '', {
    enabled: !!task?.project_id,
  });

  const form = useForm({
    initialValues: { name: '' },
    validate: {
      name: (v) => (v.trim().length > 0 ? null : 'Navn er påkrævet'),
    },
  });

  useEffect(() => {
    if (task) form.setValues({ name: task.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  const handleSave = () => {
    if (!task) return;
    updateTask.mutate(
      { taskId: task.id, updatedTask: { name: form.values.name } },
      { onSuccess: () => form.resetTouched() }
    );
  };

  if (!task) {
    return (
      <TaskDetailDrawerView
        opened={opened}
        onClose={onClose}
        loading
        titleValue=""
        onTitleChange={() => {}}
        metadata={[]}
        activeTab=""
        onTabChange={() => {}}
        overviewContent={null}
        timeContent={null}
        economyContent={null}
      />
    );
  }

  const category = categories?.find((c) => c.id === task.task_category_id);
  const trade = trades?.find((tr) => String(tr.id) === String(task.trade_id));
  const location = locations?.find((l) => l.id === task.location_id);
  const milestone = milestones?.find((m) => String(m.id) === String(task.project_milestone_id));

  return (
    <>
      <Alert
        open={deleteOpen}
        setClose={closeDelete}
        title="Bekræft sletning"
        onSubmit={() =>
          deleteTask.mutate(undefined, {
            onSuccess: () => {
              closeDelete();
              onClose();
            },
          })
        }
      >
        Er du sikker på, at du vil slette opgaven "{task.name}"? Denne handling kan ikke fortrydes.
        Sletter også: tildelte ressourcer, planlagt tid, tidsregistreringer.
      </Alert>

      <TaskDetailDrawerView
        opened={opened}
        onClose={onClose}
        titleValue={form.values.name}
        onTitleChange={(v) => form.setFieldValue('name', v)}
        saveAction={
          form.isTouched() && (
            <Button size="xs" onClick={handleSave} loading={updateTask.isPending} fullWidth>
              Gem ændringer
            </Button>
          )
        }
        metadata={[
          { label: 'Status', value: <TaskStatus task={task} size="sm" /> },
          ...(sources?.[String(task.id)]
            ? [
                {
                  label: tExtraction('source.label'),
                  value: <SourceText source={sources[String(task.id)]} />,
                },
              ]
            : []),
          ...(task.project_id
            ? [
                {
                  label: 'Projekt',
                  value: <ProjectBadge projectId={task.project_id} size="sm" withAnchor />,
                },
              ]
            : []),
          {
            label: 'Kategori',
            value: category?.name ?? 'Ingen',
            editor: (close) => (
              <SelectTaskCategory
                value={task.task_category_id}
                onChange={(v) => {
                  updateTask.mutate({ taskId: task.id, updatedTask: { task_category_id: v } });
                  close();
                }}
                clearable
              />
            ),
          },
          {
            label: t('trade.label'),
            value: trade ? (
              <Group gap={6} wrap="nowrap">
                <ColorSwatch color={trade.color} size={10} />
                {trade.name}
              </Group>
            ) : (
              t('trade.none')
            ),
            editor: (close) => (
              <SelectTrade
                value={task.trade_id}
                onChange={(v) => {
                  updateTask.mutate({ taskId: task.id, updatedTask: { trade_id: v } });
                  close();
                }}
                clearable
              />
            ),
          },

          ...(task.project_id
            ? [
                {
                  label: 'Milepæl',
                  value: milestone?.title ?? 'Ingen',
                  editor: (close: () => void) => (
                    <ProjectMilestoneSelect
                      projectId={task.project_id!}
                      value={task.project_milestone_id}
                      onChange={(v) => {
                        updateTask.mutate({
                          taskId: task.id,
                          updatedTask: { project_milestone_id: v ? Number(v) : null },
                        });
                        close();
                      }}
                      placeholder="Ingen milepæl"
                    />
                  ),
                },
                {
                  label: 'Lokation',
                  value: location?.name ?? 'Ingen',
                  editor: (close: () => void) => (
                    <SelectLocation
                      projectId={Number(task.project_id)}
                      value={task.location_id}
                      onChange={(v) => {
                        updateTask.mutate({ taskId: task.id, updatedTask: { location_id: v } });
                        close();
                      }}
                      clearable
                    />
                  ),
                },
              ]
            : []),
        ]}
        menuItems={[
          {
            label: task.archived_at ? 'Fjern arkivering' : 'Arkiver opgave',
            icon: IconArchive,
            onClick: () => (task.archived_at ? unarchive.mutate() : archive.mutate()),
          },
          { label: 'Slet opgave', icon: IconTrash, color: 'red', onClick: openDelete },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        overviewContent={<Overview task={task} onTabChange={setActiveTab} />}
        timeContent={<TimeTab task={task} />}
        economyContent={<EconomyTab task={task} />}
      />
    </>
  );
};
